import React, { useState, useMemo, useEffect } from 'react';
import { User } from '../../../../types/user';
import ModalStyles from '../../styles/Modal.module.css'; // Re-use base modal styles
import AddModalStyles from '../../styles/AddMembersModal.module.css'; // Specific styles
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore'; // Import store

interface AddMembersModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddMembers: (usernames: string[]) => Promise<void>; // Takes array of usernames
    currentMemberFqns: Set<string>;
    allTenantUsers: User[];
    isLoading?: boolean; // Indicate loading state during add
}

export const AddMembersModal: React.FC<AddMembersModalProps> = ({ 
    isOpen, 
    onClose, 
    onAddMembers, 
    currentMemberFqns, 
    allTenantUsers,
    isLoading = false
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUsernames, setSelectedUsernames] = useState<Set<string>>(new Set());
    const setSparkUserInteractionBlockingRefresh = useAtlasStore((state: AtlasState) => state.setSparkUserInteractionBlockingRefresh);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setSelectedUsernames(new Set());
        }
    }, [isOpen]);

    // Effect to block/unblock background refresh
    useEffect(() => {
        if (isOpen) {
            setSparkUserInteractionBlockingRefresh(true);
            return () => {
                setSparkUserInteractionBlockingRefresh(false);
            };
        }
    }, [isOpen, setSparkUserInteractionBlockingRefresh]);

    // Filter available users based on search and current members
    const availableUsers = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase();
        return allTenantUsers.filter(user => 
            !currentMemberFqns.has(user.fqn) &&
            (user.name.toLowerCase().includes(lowerSearch) || 
             user.username.toLowerCase().includes(lowerSearch))
        );
    }, [allTenantUsers, currentMemberFqns, searchTerm]);

    // Get full user objects for selected usernames (for chip display)
    const selectedUsers = useMemo(() => {
        return allTenantUsers.filter(user => selectedUsernames.has(user.username));
    }, [allTenantUsers, selectedUsernames]);

    const handleCheckboxChange = (username: string, isChecked: boolean) => {
        setSelectedUsernames(prev => {
            const newSet = new Set(prev);
            if (isChecked) {
                newSet.add(username);
            } else {
                newSet.delete(username);
            }
            return newSet;
        });
    };

    const handleRemoveChip = (username: string) => {
        setSelectedUsernames(prev => {
            const newSet = new Set(prev);
            newSet.delete(username);
            return newSet;
        });
    };

    const handleAddClick = async () => {
        if (selectedUsernames.size > 0) {
            await onAddMembers(Array.from(selectedUsernames));
            // onClose(); // Keep modal open until add finishes? Parent controls this.
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className={ModalStyles.modalOverlay}>
            <div className={`${ModalStyles.modalContent} ${AddModalStyles.addMembersModalContent}`}> 
                <h2>Add collaborators</h2>

                {/* Search Input */}
                <input 
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`${ModalStyles.textInput} ${AddModalStyles.searchInput}`}
                    disabled={isLoading}
                />

                {/* User List (Moved Before Chips) */}
                <div className={AddModalStyles.userListContainer}>
                    {availableUsers.length > 0 ? (
                        availableUsers.map(user => (
                            <div key={user.fqn} className={AddModalStyles.userListItem}>
                                <input 
                                    type="checkbox"
                                    id={`user-checkbox-${user.fqn}`}
                                    checked={selectedUsernames.has(user.username)}
                                    onChange={(e) => handleCheckboxChange(user.username, e.target.checked)}
                                    disabled={isLoading}
                                    className={AddModalStyles.userCheckbox}
                                />
                                <label htmlFor={`user-checkbox-${user.fqn}`} className={AddModalStyles.userLabel}>
                                    <span className={AddModalStyles.userName}>{user.name}</span>
                                    <span className={AddModalStyles.userUsername}>({user.username})</span>
                                </label>
                            </div>
                        ))
                    ) : (
                        <p className={AddModalStyles.noUsersMessage}>No matching users found.</p>
                    )}
                </div>

                {/* Selected User Chips (Moved After List) */}
                {selectedUsers.length > 0 && (
                    <div className={AddModalStyles.chipContainer}>
                        {selectedUsers.map(user => (
                            <div key={user.fqn} className={AddModalStyles.chip}>
                                {user.name} ({user.username})
                                <button 
                                    onClick={() => handleRemoveChip(user.username)} 
                                    className={AddModalStyles.chipRemove}
                                    disabled={isLoading}
                                >
                                    &times;
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal Actions */}
                <div className={ModalStyles.modalActions}>
                    <button 
                        onClick={onClose} 
                        disabled={isLoading} 
                        className={ModalStyles.buttonSecondary}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleAddClick} 
                        disabled={isLoading || selectedUsernames.size === 0}
                        className={ModalStyles.buttonPrimary}
                    >
                        {isLoading ? 'Adding...' : `Add ${selectedUsernames.size} collaborator(s)`}
                    </button>
                </div>
            </div>
        </div>
    );
}; 